import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, TextField, MenuItem, Button, Stack, Alert, CircularProgress, Divider, GlobalStyles } from "@mui/material";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import OrgApi, { OrgQuarterReport as QuarterReport, OrgQuarterReportWave } from "../api/OrgApi";
import { eur } from "../constants/funnel";

// Kwartaalrapport voor de org-admin — herschreven 2026-07 na feedback dat het
// onbegrijpelijk was: elke sectie is genummerd, heeft een titel in vraagvorm
// en begint met één zin uitleg in gewone taal; de financiële secties zijn
// samengevoegd rond één "Zo lees je dit"-rekenvoorbeeld. Inhoudelijk
// ongewijzigd: factuurgranulariteit, k-suppressie, en het rekenmodel blijft
// expliciet een schatting (review-constraint: geen ROI-claims).
const printStyles = (
  <GlobalStyles
    styles={{
      "@media print": {
        "body *": { visibility: "hidden" },
        ".orgreport-print, .orgreport-print *": { visibility: "visible" },
        ".orgreport-print": { position: "absolute", left: 0, top: 0, width: "100%", padding: "24px" },
        ".orgreport-no-print": { display: "none !important" },
      },
    }}
  />
);

const SUBSCALES = ["uitputting", "mentaleDistantie", "cognitieveOntregeling", "emotioneleOntregeling"] as const;

/** Eén rapportsectie: nummer + vraag-titel + één zin gewone-mensen-uitleg. */
function Section({ nr, title, explain, children }: { nr: number; title: string; explain: string; children: React.ReactNode }) {
  return (
    <Box sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: "var(--radius-lg)", border: "1px solid var(--border)", bgcolor: "var(--surface)" }}>
      <Typography variant="h3" sx={{ fontSize: "1.05rem", m: 0 }}>
        <Box component="span" sx={{ color: "primary.main", mr: 1 }}>{nr}.</Box>
        {title}
      </Typography>
      <Typography sx={{ color: "text.secondary", fontSize: ".9rem", mt: 0.5, mb: 2 }}>{explain}</Typography>
      {children}
    </Box>
  );
}

/** Kerncijfer-kaartje voor "In één oogopslag". */
function Glance({ value, label, note }: { value: string; label: string; note?: string }) {
  return (
    <Box sx={{ flex: "1 1 150px", p: 2, borderRadius: "var(--radius-md)", bgcolor: "var(--primary-50)", border: "1px solid var(--primary-100)" }}>
      <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, m: 0, color: "primary.dark" }}>{value}</Typography>
      <Typography sx={{ fontSize: ".85rem", m: 0 }}>{label}</Typography>
      {note && <Typography sx={{ fontSize: ".75rem", color: "text.secondary", m: 0 }}>{note}</Typography>}
    </Box>
  );
}

function WaveSummary({ wave, k }: { wave: OrgQuarterReportWave; k: number }) {
  const { t } = useTranslation();
  if (wave.suppressed || !wave.aggregate) {
    return (
      <Box>
        <Typography sx={{ fontWeight: 600, m: 0 }}>{wave.title}</Typography>
        <Typography sx={{ color: "text.secondary", fontSize: ".9rem", m: 0 }}>{t("barometer.suppressedReport", { k })}</Typography>
      </Box>
    );
  }
  const a = wave.aggregate;
  return (
    <Box>
      <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{wave.title}</Typography>
      <Stack spacing={0.5}>
        {a.welzijn && (
          <Typography sx={{ m: 0, fontSize: ".95rem" }}>
            {t("barometer.welzijnScore")}: <strong>{a.welzijn.nowMean}</strong> · {t("barometer.welzijnFuture")}: <strong>{a.welzijn.futureMean}</strong> ({t("barometer.nLabel", { n: a.welzijn.n })})
          </Typography>
        )}
        {a.burnout && (
          <Typography sx={{ m: 0, fontSize: ".95rem" }}>
            {t("barometer.batReportTitle")}: <strong>{a.burnout.overallMean}</strong>
            {" — "}{SUBSCALES.map((s) => `${t(`barometer.sub.${s}`)} ${a.burnout!.subscales[s]}`).join(" · ")} ({t("barometer.nLabel", { n: a.burnout.n })})
          </Typography>
        )}
        {a.werkbeleving && (
          <Typography sx={{ m: 0, fontSize: ".95rem" }}>
            {t("barometer.werkReportTitle")}: <strong>{a.werkbeleving.overallMean}</strong> ({t("barometer.nLabel", { n: a.werkbeleving.n })})
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export default function OrgQuarterReport() {
  const { t, i18n } = useTranslation();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(Math.floor(now.getMonth() / 3) + 1);
  const [report, setReport] = useState<QuarterReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");

  const load = useCallback(async (y: number, q: number) => {
    setLoading(true);
    setErrMsg("");
    try { setReport(await OrgApi.quarterReport(y, q)); }
    catch { setErrMsg(t("orgReport.loadError")); setReport(null); }
    finally { setLoading(false); }
  }, [t]);
  useEffect(() => { load(year, quarter); }, [year, quarter, load]);

  const monthName = (m: number) => new Intl.DateTimeFormat(i18n.language, { month: "long" }).format(new Date(2024, m - 1, 1));
  const years = Array.from({ length: now.getFullYear() - 2024 + 1 }, (_, i) => 2024 + i).reverse();

  return (
    <Stack spacing={2.5}>
      {printStyles}
      <Typography sx={{ color: "text.secondary", m: 0 }} className="orgreport-no-print">{t("orgReport.intro")}</Typography>

      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", flexWrap: "wrap", gap: 1.5 }} className="orgreport-no-print">
        <TextField select size="small" label={t("orgReport.yearLabel")} value={year} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setYear(Number(e.target.value))} sx={{ width: 120 }}>
          {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
        <TextField select size="small" label={t("orgReport.quarterLabel")} value={quarter} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuarter(Number(e.target.value))} sx={{ width: 120 }}>
          {[1, 2, 3, 4].map((q) => <MenuItem key={q} value={q}>Q{q}</MenuItem>)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" startIcon={<PrintRoundedIcon />} onClick={() => window.print()} disabled={!report}>{t("orgReport.print")}</Button>
      </Stack>

      {errMsg && <Alert severity="error" onClose={() => setErrMsg("")}>{errMsg}</Alert>}
      {loading && <CircularProgress size={24} sx={{ color: "primary.main" }} />}

      {!loading && report && (
        <Stack spacing={2.5} className="orgreport-print">
          <Box>
            <Typography variant="h3" sx={{ fontSize: "1.2rem", m: 0 }}>{t("orgReport.title", { quarter: report.quarter, year: report.year })}</Typography>
            <Typography sx={{ color: "text.secondary", fontSize: ".85rem", m: 0 }}>{t("orgReport.granularityNote")}</Typography>
          </Box>

          {/* In één oogopslag — alleen tonen als er iets te tonen ís: een
              kwartaal zonder gesprekken krijgt één vriendelijke zin, geen
              rij nullen ("€0 geschatte waarde" zegt precies het verkeerde). */}
          {report.totals.totalSessions === 0 ? (
            <Alert severity="info">{t("orgReport.glance.empty")}</Alert>
          ) : (
            <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap", gap: 1.5 }}>
              <Glance value={String(report.totals.totalSessions)} label={t("orgReport.glance.sessions")} />
              <Glance value={eur(report.totals.totalCostExclVatCents)} label={t("orgReport.glance.cost")} note={t("orgReport.exclVat")} />
              <Glance value={eur(report.financialImpact.costPerEmployeeCents)} label={t("orgReport.glance.perEmployee")} note={t("orgReport.glance.perEmployeeNote", { headcount: report.financialImpact.headcount })} />
              {report.modelledEstimate.modelledValueCents + report.financialImpact.careValueCents > 0 && (
                <Glance value={eur(report.modelledEstimate.modelledValueCents + report.financialImpact.careValueCents)} label={t("orgReport.glance.value")} note={t("orgReport.glance.valueNote")} />
              )}
            </Stack>
          )}

          <Section nr={1} title={t("orgReport.s1.title")} explain={t("orgReport.s1.explain")}>
            <Stack divider={<Divider />} spacing={0}>
              {report.months.map((m) => (
                <Box key={m.month} sx={{ display: "flex", gap: 2, py: 0.75, alignItems: "baseline" }}>
                  <Typography sx={{ m: 0, width: 110, textTransform: "capitalize" }}>{monthName(m.month)}</Typography>
                  <Typography sx={{ m: 0, flex: 1, color: "text.secondary", fontSize: ".95rem" }}>
                    {Object.entries(m.countsByType).map(([type, n]) => `${t(`booking.type.${type}`)}: ${n}`).join(" · ") || t("orgReport.noSessions")}
                  </Typography>
                  <Typography sx={{ m: 0, fontWeight: 600 }}>{m.totalSessions}</Typography>
                </Box>
              ))}
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Typography sx={{ m: 0 }}>{t("orgReport.totalSessions")}: <strong>{report.totals.totalSessions}</strong></Typography>
          </Section>

          <Section nr={2} title={t("orgReport.s2.title")} explain={t("orgReport.s2.explain", { k: report.adoption.kThreshold })}>
            <Typography sx={{ m: 0 }}>{t("org.members")}: <strong>{report.adoption.memberCount}</strong></Typography>
            {report.adoption.suppressed ? (
              <Typography sx={{ color: "text.secondary", m: 0 }}>{t("orgReport.adoptionSuppressed", { k: report.adoption.kThreshold })}</Typography>
            ) : (
              <Typography sx={{ m: 0 }}>{t("orgReport.activeMembers")}: <strong>{report.adoption.activeMembers}</strong></Typography>
            )}
            {report.benchmark.adoptionPct !== null && (
              <Typography sx={{ m: 0, mt: 0.5, fontSize: ".9rem", color: "text.secondary" }}>
                {t("orgReport.benchAdoption", { pct: report.benchmark.adoptionPct, orgs: report.benchmark.orgs })}
              </Typography>
            )}
          </Section>

          <Section nr={3} title={t("orgReport.s3.title")} explain={t("orgReport.s3.explain")}>
            {report.barometer.waves.length === 0 ? (
              <Typography sx={{ color: "text.secondary", m: 0 }}>{t("orgReport.noWaves")}</Typography>
            ) : (
              <Stack spacing={1.5}>
                {report.barometer.waves.map((w) => <WaveSummary key={w.id} wave={w} k={report.barometer.kThreshold} />)}
                {report.outcome && !report.outcome.suppressed && (
                  <Typography sx={{ m: 0, fontSize: ".95rem" }}>
                    {t("orgReport.outcomeLine", { progress: report.outcome.progressMean, usefulness: report.outcome.usefulnessMean, n: report.outcome.n })}
                  </Typography>
                )}
                {report.outcome && report.outcome.suppressed && (
                  <Typography sx={{ m: 0, fontSize: ".85rem", color: "text.secondary" }}>{t("orgReport.outcomeSuppressed", { k: report.outcome.kThreshold })}</Typography>
                )}
                {report.benchmark.werkbeleving !== null && (
                  <Typography sx={{ m: 0, fontSize: ".9rem", color: "text.secondary" }}>
                    {t("orgReport.benchWerkbeleving", { score: report.benchmark.werkbeleving, orgs: report.benchmark.orgs })}
                  </Typography>
                )}
                <Box>
                  {(["welzijn", "burnout", "werkbeleving"] as const)
                    .filter((m) => report.barometer.waves.some((w) => w.aggregate?.[m]))
                    .map((m) => <Typography key={m} sx={{ fontSize: ".75rem", color: "text.secondary", m: 0 }}>{t(`barometer.cite.${m}`)}</Typography>)}
                </Box>
              </Stack>
            )}
          </Section>

          {/* Kosten + waarde samengevoegd: eerst de factuur in gewone taal,
              dan één "Zo lees je dit"-rekenvoorbeeld dat zorgwaarde en het
              verzuim-model in één verhaal uitlegt. */}
          <Section nr={4} title={t("orgReport.s4.title")} explain={t("orgReport.s4.explain")}>
            {report.totals.totalSessions === 0 && (
              <Typography sx={{ color: "text.secondary", m: 0, mb: 1.5 }}>{t("orgReport.s4.empty")}</Typography>
            )}
            <Stack spacing={0.75} sx={{ mb: 2 }}>
              <Typography sx={{ m: 0 }}>{t("orgReport.serviceFees")}: <strong>{eur(report.totals.employerFeeCents)}</strong> <Box component="span" sx={{ color: "text.secondary", fontSize: ".85rem" }}>{t("orgReport.s4.serviceFeesPlain")}</Box></Typography>
              <Typography sx={{ m: 0 }}>{t("orgReport.passThrough")}: <strong>{eur(report.totals.passThroughCents)}</strong> <Box component="span" sx={{ color: "text.secondary", fontSize: ".85rem" }}>{t("orgReport.s4.passThroughPlain")}</Box></Typography>
              <Typography sx={{ m: 0 }}>{t("orgReport.annualFeeQuarter", { tier: report.annualFee.tierLabel })}: <strong>{eur(report.totals.annualFeeQuarterCents)}</strong> <Box component="span" sx={{ color: "text.secondary", fontSize: ".85rem" }}>{t("orgReport.s4.annualFeePlain")}</Box></Typography>
              <Divider sx={{ my: 0.5 }} />
              <Typography sx={{ m: 0 }}>{t("orgReport.totalCost")}: <strong>{eur(report.totals.totalCostExclVatCents)}</strong> <Box component="span" sx={{ color: "text.secondary", fontSize: ".85rem" }}>{t("orgReport.exclVat")}</Box></Typography>
            </Stack>

            {/* Het rekenvoorbeeld: één doorlopend verhaal in gewone taal —
                alleen als er iets te vertellen valt. */}
            {report.totals.totalSessions > 0 && (
            <Box sx={{ p: 2, borderRadius: "var(--radius-md)", bgcolor: "var(--primary-50)", border: "1px solid var(--primary-100)", mb: 1.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: ".95rem", mb: 1 }}>{t("orgReport.s4.exampleTitle")}</Typography>
              <Typography sx={{ fontSize: ".95rem", m: 0, lineHeight: 1.7 }}>
                {t("orgReport.s4.exampleStory", {
                  sessions: report.modelledEstimate.sessionsInPeriod,
                  cost: eur(report.totals.totalCostExclVatCents),
                  careValue: eur(report.financialImpact.careValueCents),
                  days: report.modelledEstimate.modelledAvoidedAbsenceDays,
                  dayCost: eur(report.modelledEstimate.assumptions.absenceDayCostCents),
                  modelValue: eur(report.modelledEstimate.modelledValueCents),
                })}
              </Typography>
            </Box>
            )}

            {report.totals.totalSessions > 0 && <Alert severity="info" sx={{ mb: 1.5 }}>{t("orgReport.modelledDisclaimer")}</Alert>}
            <Typography sx={{ color: "text.secondary", fontSize: ".85rem", mb: 0.5 }}>{t("orgReport.assumptionsTitle")}</Typography>
            <Box component="ul" sx={{ m: 0, pl: 3, color: "text.secondary", fontSize: ".85rem" }}>
              <li>{t("orgReport.assumptionAvoidedDays", { days: report.modelledEstimate.assumptions.avoidedAbsenceDaysPerSession })}</li>
              <li>{t("orgReport.assumptionDayCost", { cost: eur(report.modelledEstimate.assumptions.absenceDayCostCents) })}</li>
            </Box>
          </Section>

          {report.totals.totalSessions > 0 && (
          <Section nr={5} title={t("orgReport.s5.title")} explain={t("orgReport.s5.explain")}>
            <Box sx={{ overflowX: "auto" }}>
              <Box component="table" sx={{ borderCollapse: "collapse", width: "100%", minWidth: 640, "& th, & td": { textAlign: "left", p: 1, borderBottom: "1px solid var(--border)", fontSize: ".9rem" }, "& th": { color: "text.secondary", fontWeight: 600 } }}>
                <thead>
                  <tr>
                    <th>{t("orgReport.impact.colBenefit")}</th>
                    <th>{t("orgReport.impact.colCost")}</th>
                    <th>{t("orgReport.impact.colAdoption")}</th>
                    <th>{t("orgReport.impact.colCostPerUser")}</th>
                    <th>{t("orgReport.impact.colValuePerUser")}</th>
                    <th>{t("orgReport.impact.colValueTransfer")}</th>
                  </tr>
                </thead>
                <tbody>
                  <Box component="tr" sx={{ "& td": { fontWeight: 600, bgcolor: "var(--primary-50)" } }}>
                    <td>Carefunded</td>
                    <td>{eur(report.financialImpact.costPerEmployeeCents * 4)} {t("orgReport.impact.extrapolated")}</td>
                    <td>{report.financialImpact.adoptionPctOfHeadcount === null ? t("orgReport.impact.adoptionSuppressed") : `${report.financialImpact.adoptionPctOfHeadcount}%`}</td>
                    <td>{report.financialImpact.effectiveCostPerUserCents === null ? t("orgReport.impact.adoptionSuppressed") : `${eur(report.financialImpact.effectiveCostPerUserCents * 4)} ${t("orgReport.impact.extrapolated")}`}</td>
                    <td>{report.adoption.activeMembers === null ? t("orgReport.impact.adoptionSuppressed") : `${eur(Math.round(report.financialImpact.modelledTotalValueCents / report.adoption.activeMembers) * 4)} ${t("orgReport.impact.extrapolatedModelled")}`}</td>
                    <td>{t("orgReport.impact.carefundedValue")}</td>
                  </Box>
                  {(["meal", "fitness", "car"] as const).map((k) => (
                    <tr key={k}>
                      <td>{t(`orgReport.impact.bench.${k}.name`)}</td>
                      <td>{t(`orgReport.impact.bench.${k}.cost`)}</td>
                      <td>{t(`orgReport.impact.bench.${k}.adoption`)}</td>
                      <td>{t(`orgReport.impact.bench.${k}.costPerUser`)}</td>
                      <td>{t(`orgReport.impact.bench.${k}.valuePerUser`)}</td>
                      <td>{t(`orgReport.impact.bench.${k}.value`)}</td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
            <Typography sx={{ fontSize: ".8rem", color: "text.secondary", mt: 1, mb: 0 }}>{t("orgReport.impact.benchmarkDisclaimer")}</Typography>
          </Section>
          )}

          <Typography sx={{ color: "text.secondary", fontSize: ".8rem", m: 0 }}>{t("orgReport.printFooter")}</Typography>
        </Stack>
      )}
    </Stack>
  );
}
